import apiClient, { ApiResponse } from "./client";
import { config } from "../config";
import { notify } from "../utils/toast";

export interface BachelorDegree {
	id: string;
	majorCode: string; // Mã Ngành
	major: string; // Ngành
	cccd: string; // CCCD
	studentId: string; // Mã sinh viên
	studentName: string; // Họ và tên
	dateOfBirth: string; // Ngày sinh
	placeOfBirth: string; // Nơi sinh
	gender: string; // Giới tính
	ethnicCode: string; // Mã Dân tộc
	ethnicity: string; // Dân tộc
	nationalityCode: string; // Mã quốc tịch
	nationality: string; // quốc tịch
	graduationYear: string; // Năm tốt nghiệp
	classification: string; // Xếp loại tốt nghiệp
	classificationId: string; // Xếp loại tốt nghiệp ID
	trainingMode: string; // Hình thức đào tạo
	degreeNumber: string; // Số hiệu bằng (cơ sở)
	registrationNumber: string; // Số vào sổ cấp bằng
	signedBy: string; // Người ký
	signerTitle: string; // Chức danh người ký
	signDate?: string; // Ngày ký
	course: string; // Khoá học
	recognitionDecisionNumber: string; // Quyết định công nhận số
	requestTypeId: string; // Loại yêu cầu ID
	requestType: string; // Loại yêu cầu
	notes?: string; // Ghi chú
	status: "Nội bộ" | "Đang gửi" | "Đã xác nhận" | "Đã thu hồi"; // Trạng thái hệ thống
	createdAt?: string;
	updatedAt?: string;
}

export interface BachelorDegreeFilters {
	search?: string;
	requestType?: string;
	major?: string;
	trainingMode?: string;
	yearFrom?: string;
	yearTo?: string;
	status?: string;
	classification?: string;
	page?: number;
	limit?: number;
}

export interface BachelorDegreeListResponse {
	data: BachelorDegree[];
	total: number;
	page: number;
	totalPages: number;
	limit: number;
}

export interface BulkImportResponse {
	created: BachelorDegree[];
	count: number;
	errors?: { row: number; message: string }[];
}

export const bachelorDegreesApi = {
	// Get all bachelor degrees with optional filters
	count: async ()=> {
		return await apiClient.get<number>(`${config.endpoints.bachelorDegrees}/count`);
	},
	getAll: async (filters?: BachelorDegreeFilters): Promise<ApiResponse<BachelorDegreeListResponse>> => {
		try {
			const response = await apiClient.get<BachelorDegreeListResponse>(config.endpoints.bachelorDegrees, filters);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			console.warn("API call failed, using mock data:", error);
			throw error;
		}
	},

	// Get bachelor degree by ID
	getById: async (id: string, field?: string, log: boolean = true): Promise<ApiResponse<BachelorDegree>> => {
		try {
			const response = await apiClient.get<BachelorDegree>(`${config.endpoints.bachelorDegrees}/${id}?field=${field}`);
			return response;
		} catch (error) {
			if (error instanceof Error && log) {
				notify.error(error.message);
			}
			console.error("Error fetching bachelor degree:", error);
			throw error;
		}
	},

	// Create new bachelor degree
	create: async (degree: Omit<BachelorDegree, "id" | "createdAt" | "updatedAt">): Promise<ApiResponse<BachelorDegree>> => {
		try {
			const response = await apiClient.post<BachelorDegree>(config.endpoints.bachelorDegrees, degree);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			console.error("Error creating bachelor degree:", error);
			throw error;
		}
	},

	// Update bachelor degree
	update: async (id: string, degree: Partial<BachelorDegree>): Promise<ApiResponse<BachelorDegree>> => {
		try {
			const response = await apiClient.put<BachelorDegree>(`${config.endpoints.bachelorDegrees}/${id}`, degree);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			console.error("Error updating bachelor degree:", error);
			throw error;
		}
	},

	// Delete bachelor degree
	delete: async (id: string): Promise<ApiResponse<void>> => {
		try {
			const response = await apiClient.delete<void>(`${config.endpoints.bachelorDegrees}/${id}`);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			console.error("Error deleting bachelor degree:", error);
			throw error;
		}
	},

	// Bulk import bachelor degrees
	bulkImport: async (degrees: Omit<BachelorDegree, "id" | "createdAt" | "updatedAt">[]): Promise<ApiResponse<BulkImportResponse>> => {
		try {
			const response = await apiClient.post<BulkImportResponse>(`${config.endpoints.bachelorDegrees}/bulk-import`, degrees);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			console.error("Error bulk importing bachelor degrees:", error);
			throw error;
		}
	},

	// Export bachelor degrees
	export: async (filters?: BachelorDegreeFilters): Promise<ApiResponse<BachelorDegree[]>> => {
		try {
			const response = await apiClient.get<BachelorDegree[]>(`${config.endpoints.bachelorDegrees}/export`, filters);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			console.error("Error exporting bachelor degrees:", error);
			throw error;
		}
	},

	// Search public bachelor degrees (for public search page)
	searchPublic: async (query: { cccd?: string; studentId?: string; degreeNumber?: string }): Promise<ApiResponse<BachelorDegree[]>> => {
		try {
			const response = await apiClient.get<BachelorDegree[]>(`${config.endpoints.public.search}/bachelor-degrees`, query);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				notify.error(error.message);
			}
			console.error("Error searching bachelor degrees:", error);
			throw error;
		}
	},

	checkExist: async ({ searchValue, field }: { searchValue: string; field: "degreeNumber" | "studentId" }) => {
		const response = await apiClient.get<boolean>(`${config.endpoints.bachelorDegrees}/check-exist`, { search_value: searchValue, field });
		return response;
	},

	checkCitizenInfoExist: async ({ citizenId, majorCode }: { citizenId: string; majorCode: string }) => {
		const response = await apiClient.get<boolean>(`${config.endpoints.bachelorDegrees}/check-citizen-exist`, { search_value: citizenId, major_code: majorCode });
		return response;
	},
};
